import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorshipMeeting {
  id: string;
  mentorship_request_id: string;
  thread_id: string;
  proposed_by: string;
  proposed_duration: number;
  proposed_note: string | null;
  proposed_availability: string | null;
  status: "proposed" | "scheduled" | "cancelled" | "completed";
  responded_at: string | null;
  response_note: string | null;
  zoom_meeting_id: string | null;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
  scheduled_at: string | null;
  scheduled_start_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MentorshipRequestWithDetails {
  id: string;
  entrepreneur_id: string;
  mentor_id: string;
  topic_id: string;
  message: string;
  status: string;
  thread_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  completed_by: string | null;
  completion_note: string | null;
  entrepreneur_reflection: string | null;
  entrepreneur_thank_you: string | null;
  entrepreneur?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  mentor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  topic?: {
    id: string;
    name: string;
  };
}

// Fetch mentorship meetings for a specific thread (for history/debugging)
export function useMentorshipMeetings(threadId?: string) {
  return useQuery({
    queryKey: ["mentorship-meetings", threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data, error } = await supabase
        .from("mentorship_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as MentorshipMeeting[];
    },
    enabled: !!threadId,
  });
}

// Get mentorship request details for a thread
export function useMentorshipRequestByThread(threadId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-request-by-thread", threadId],
    queryFn: async () => {
      if (!threadId) return null;

      const { data, error } = await supabase
        .from("mentorship_requests")
        .select(`
          *,
          entrepreneur:profiles!mentorship_requests_entrepreneur_id_fkey(id, full_name, avatar_url),
          mentor:profiles!mentorship_requests_mentor_id_fkey(id, full_name, avatar_url),
          topic:mentor_topics(id, name)
        `)
        .eq("thread_id", threadId)
        .eq("status", "accepted")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Transform to expected format
      return {
        ...data,
        entrepreneur: Array.isArray(data.entrepreneur) ? data.entrepreneur[0] : data.entrepreneur,
        mentor: Array.isArray(data.mentor) ? data.mentor[0] : data.mentor,
        topic: Array.isArray(data.topic) ? data.topic[0] : data.topic,
      } as MentorshipRequestWithDetails;
    },
    enabled: !!threadId && !!user?.id,
  });
}

// Complete mentorship - now uses thread_id via mentorship_relationships table
export function useCompleteMentorship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      threadId,
      completionNote,
    }: {
      threadId: string;
      completionNote?: string;
    }) => {
      const { data, error } = await supabase.rpc("complete_mentorship", {
        p_thread_id: threadId,
        p_note: completionNote || null,
      });

      if (error) throw error;
      
      // Check RPC response for errors - cast to expected shape
      const result = data as { success: boolean; error?: string } | null;
      if (result && !result.success) {
        throw new Error(result.error || "Failed to complete mentorship");
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-request-by-thread"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-relationships"] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      toast.success("Mentorship completed — connection remains active.");
    },
    onError: (error) => {
      toast.error("Failed to complete mentorship: " + error.message);
    },
  });
}

// Submit entrepreneur reflection
export function useSubmitReflection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      reflection,
      thankYou,
    }: {
      requestId: string;
      reflection?: string;
      thankYou?: string;
    }) => {
      const { error } = await supabase.rpc("submit_mentorship_reflection", {
        p_request_id: requestId,
        p_reflection: reflection || null,
        p_thank_you: thankYou || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-request-by-thread"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      toast.success("Thank you for your feedback!");
    },
    onError: (error) => {
      toast.error("Failed to submit reflection: " + error.message);
    },
  });
}
